// Vista "Tabla" bajo el mapa (pura, con tests).
//
// Es la "Detalles" de la plataforma anterior: todas las unidades en renglones,
// como hoja de cálculo, para comparar de un vistazo sin abrir una por una. Lo
// que decide qué se ve —orden, qué renglones dibujar, qué columnas caben y la
// altura del panel— vive acá para probarlo sin montar el mapa.

import type { VehicleSocket } from "../../../../store/admin/vehiclesSocketStore";
import { liveSpeedKmh, readEngine } from "../Sidebar/engineDisplay";

export type ColumnKey =
  | "name"
  | "status"
  | "speed"
  | "engine"
  | "lastReport"
  | "odometer"
  | "driver"
  | "group";

export type SortDir = "asc" | "desc";
export interface SortState {
  key: ColumnKey;
  dir: SortDir;
}

// Orden "natural" de cada estado: primero lo que se mueve, al final lo que no
// reporta. Es el mismo orden de las pestañas de estado del panel.
const STATUS_RANK: Record<string, number> = { driving: 0, ralenti: 1, stopped: 2, offline: 3 };
const ENGINE_RANK: Record<string, number> = { on: 0, off: 1, unknown: 2, noData: 3 };

const driverName = (v: VehicleSocket) => {
  const n = (v as { driver?: { name?: string } }).driver?.name;
  return n && n !== "Sin asignar" ? n : null;
};

export const unitName = (v: VehicleSocket) => v.vehicle?.name || v.imei || v.id;

const sortValue = (v: VehicleSocket, key: ColumnKey): string | number | null => {
  switch (key) {
    case "name":
      return unitName(v);
    case "status":
      return STATUS_RANK[v.status] ?? 4;
    case "speed":
      return liveSpeedKmh(v.speed, v.status);
    case "engine":
      return ENGINE_RANK[readEngine(v.speed, v.status, v.engineStatus).tone];
    case "lastReport":
      return v.lastReport?._seconds > 0 ? v.lastReport._seconds : null;
    case "odometer":
      return v.odometer ?? null;
    case "driver":
      return driverName(v);
    case "group":
      return v.group || null;
  }
};

const collator = new Intl.Collator("es", { numeric: true, sensitivity: "base" });

// Los vacíos van siempre al final, en las dos direcciones: quien ordena por
// odómetro quiere ver números, no 40 "Sin datos" arriba. Orden estable: a
// igualdad se respeta el orden de la lista (el mismo del panel).
export function sortVehicles(list: VehicleSocket[], sort: SortState | null): VehicleSocket[] {
  if (!sort) return list;
  const mul = sort.dir === "asc" ? 1 : -1;
  return list
    .map((v, i) => ({ v, i, k: sortValue(v, sort.key) }))
    .sort((a, b) => {
      if (a.k == null && b.k == null) return a.i - b.i;
      if (a.k == null) return 1;
      if (b.k == null) return -1;
      const c =
        typeof a.k === "number" && typeof b.k === "number"
          ? a.k - b.k
          : collator.compare(String(a.k), String(b.k));
      return c !== 0 ? c * mul : a.i - b.i;
    })
    .map((x) => x.v);
}

// Clic en el encabezado: ascendente → descendente → sin orden (vuelve al de
// la lista). "Última conexión" empieza descendente: lo primero que se quiere
// ver es lo más reciente.
export const nextSort = (cur: SortState | null, key: ColumnKey): SortState | null => {
  const first: SortDir = key === "lastReport" ? "desc" : "asc";
  if (!cur || cur.key !== key) return { key, dir: first };
  if (cur.dir === first) return { key, dir: first === "asc" ? "desc" : "asc" };
  return null;
};

// Renglones de alto fijo y sólo se dibujan los visibles (+ margen): con 1,000+
// unidades dibujar todo traba el scroll y el mapa a la vez.
export const ROW_HEIGHT = 36;
export function visibleRange(
  scrollTop: number,
  viewport: number,
  count: number,
  rowHeight = ROW_HEIGHT,
  overscan = 8,
): { start: number; end: number } {
  if (count <= 0 || viewport <= 0) return { start: 0, end: Math.min(count, overscan) };
  const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan);
  const end = Math.min(count, Math.ceil((scrollTop + viewport) / rowHeight) + overscan);
  return { start, end };
}

// Columnas por prioridad. El nombre, el estado, la velocidad y la última
// conexión son lo que se compara de un vistazo y nunca se ocultan. Si el panel
// es angosto (lista de unidades abierta en una laptop de 1366 px deja ~800 px
// para el mapa), primero se van grupo y conductor, después motor y odómetro.
export const COLUMN_WIDTH: Record<ColumnKey, number> = {
  name: 180,
  status: 120,
  speed: 108,
  engine: 104,
  lastReport: 140,
  odometer: 112,
  driver: 168,
  group: 136,
};
const ORDER: ColumnKey[] = ["name", "status", "speed", "lastReport", "engine", "odometer", "driver", "group"];
const DROP_ORDER: ColumnKey[] = ["group", "driver", "engine", "odometer"];
export const ACTIONS_WIDTH = 48;

export function visibleColumns(width: number): ColumnKey[] {
  const cols = [...ORDER];
  const total = () => cols.reduce((s, c) => s + COLUMN_WIDTH[c], ACTIONS_WIDTH);
  for (const drop of DROP_ORDER) {
    if (width <= 0 || total() <= width) break;
    cols.splice(cols.indexOf(drop), 1);
  }
  return cols;
}

// Alto del panel: lo arrastra el usuario y se recuerda. Nunca menos de un
// encabezado + 3 renglones (para que siga siendo tabla) ni más del 70% del
// área: el mapa tiene que seguir viéndose para que el clic en un renglón
// tenga dónde llevar.
export const MIN_TABLE_HEIGHT = 44 + 36 + ROW_HEIGHT * 3;
export const clampTableHeight = (h: number, area: number) =>
  Math.round(Math.max(MIN_TABLE_HEIGHT, Math.min(h, Math.max(MIN_TABLE_HEIGHT, area * 0.7))));
export const defaultTableHeight = (area: number) => clampTableHeight(area * 0.36, area);

// Excel de la tabla: TODAS las columnas (aunque en pantalla no quepan) y en el
// orden y filtro que se ven, más la ubicación, que en pantalla sólo se copia.
// Velocidad y odómetro van como número para que en Excel se puedan sumar y
// ordenar; el vacío es "" y no «—», que Excel tomaría como texto.
export interface ExportRow {
  id: string;
  name: string;
  status: string;
  speed: number | "";
  engine: string;
  lastReport: string;
  odometer: number | "";
  driver: string;
  group: string;
  location: string;
}

export function exportRows(
  list: VehicleSocket[],
  label: { status: (s: string) => string; engine: (tone: string) => string },
  fmtDate: (ms: number) => string,
): ExportRow[] {
  return list.map((v) => {
    const speed = liveSpeedKmh(v.speed, v.status);
    const secs = v.lastReport?._seconds;
    const lat = Number(v.latitude);
    const lng = Number(v.longitude);
    return {
      id: v.id,
      name: String(unitName(v)),
      status: label.status(v.status),
      speed: speed == null ? "" : Math.round(speed),
      engine: label.engine(readEngine(v.speed, v.status, v.engineStatus).tone),
      lastReport: secs > 0 ? fmtDate(secs * 1000) : "",
      odometer: v.odometer != null ? Math.round(v.odometer) : "",
      driver: driverName(v) ?? "",
      group: v.group || "",
      location: lat && lng && Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : "",
    };
  });
}
